import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Eye, EyeSlash, ArrowUp, ArrowsLeftRight, Plus, Warning } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const shapeLabel = (st) => {
  const s = String(st || "").replace("_fallback", "").replace("_relaxed", "");
  return s === "primary_only" ? "1-game" : s === "primary_secondary" ? "2-game" : s === "primary_secondary_tertiary" ? "3-game" : s || "—";
};
const money = (v) => (v == null ? "—" : `$${Number(v).toFixed(2)}`);
const SORTS = [
  { key: "payout_ev", label: "$EV" },
  { key: "projection", label: "Proj" },
  { key: "total_own", label: "Own" },
  { key: "stack_type", label: "Shape" },
];
const REASONS = [
  { key: "all", label: "All" },
  { key: "below_cut", label: "Below cut line" },
  { key: "exposure_cap", label: "Exposure cap" },
  { key: "too_similar", label: "Too similar" },
  { key: "taper", label: "Chalk taper" },
  { key: "portfolio_tradeoff", label: "Portfolio trade-off" },
  { key: "demoted", label: "Demoted" },
];

// Sprint 71 — Smart-only Candidate Explorer: browse sim-rejected overbuild
// candidates and promote one into the portfolio (swap replaces the lowest-$EV
// lineup by default; the user sees exactly which lineup drops before confirming).
export const CandidateExplorer = ({ onChanged }) => {
  const [open, setOpen] = useState(() => localStorage.getItem("wp_cand_explorer_open") === "1");
  const [data, setData] = useState(null);
  const [sort, setSort] = useState("payout_ev");
  const [reason, setReason] = useState("all");
  const [pending, setPending] = useState(null); // { cand, mode, victimId }
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { setData(await api.getCandidates()); } catch { setData({ rejected: [], portfolio: [] }); }
  };
  useEffect(() => { load(); }, []);

  const rejected = data?.rejected || [];
  const portfolio = data?.portfolio || [];
  const lowest = useMemo(() => portfolio.length
    ? portfolio.reduce((a, b) => ((Number(b.payout_ev) || 0) < (Number(a.payout_ev) || 0) ? b : a))
    : null, [portfolio]);

  const rows = useMemo(() => {
    let r = reason === "all" ? rejected : rejected.filter((c) => c.reject_code === reason);
    return [...r].sort((a, b) => sort === "stack_type"
      ? String(a.stack_type || "").localeCompare(String(b.stack_type || ""))
      : (Number(b[sort]) || 0) - (Number(a[sort]) || 0));
  }, [rejected, sort, reason]);

  const toggle = () => setOpen((v) => { localStorage.setItem("wp_cand_explorer_open", v ? "0" : "1"); return !v; });

  const startPromote = (cand, mode) => setPending({ cand, mode, victimId: lowest?.lineup_id ?? null });

  const confirm = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      const res = await api.promoteCandidate({
        candidate_id: pending.cand.candidate_id, mode: pending.mode,
        replace_lineup_id: pending.mode === "swap" ? pending.victimId : null,
      });
      const msg = pending.mode === "swap"
        ? `Promoted candidate #${pending.cand.candidate_id} → replaced Lineup ${res.replaced?.lineup_id} (${money(res.replaced?.payout_ev)} $EV)`
        : `Added candidate #${pending.cand.candidate_id} as Lineup ${res.promoted_lineup_id}`;
      toast.success(msg, {
        duration: 12000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.undoPromote();
              toast.success(pending.mode === "swap" ? `Undone · Lineup ${res.replaced?.lineup_id} is back` : "Undone · added lineup removed");
              await load();
              onChanged?.();
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Nothing to undo");
            }
          },
        },
      });
      for (const w of res.exposure_warnings || []) toast.warning(`Exposure: ${w}`);
      setPending(null);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Promote failed");
    } finally {
      setBusy(false);
    }
  };

  if (!data) return null;
  const n = rejected.length;
  return (
    <section className="bg-[#121212] border border-zinc-800" data-testid="candidate-explorer">
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="overline">Candidate Explorer · Smart only</div>
          <div className="text-sm text-zinc-300 mt-1">
            {n
              ? `${n} rule-legal candidates the sim passed over · cut line ${money(data.cut_ev)} $EV`
              : "No rejected candidates yet — run a Smart GPP / Milly build with sim selection on"}
          </div>
        </div>
        <button type="button" onClick={toggle} data-testid="candidate-explorer-toggle" aria-expanded={open}
                className={`inline-flex items-center gap-2 px-3 py-1.5 border mono text-[10px] uppercase tracking-widest ${open ? "bg-[#39FF14] text-black border-[#39FF14] font-bold" : "border-zinc-700 text-zinc-300 hover:border-[#39FF14]"}`}>
          {open ? <EyeSlash size={13} /> : <Eye size={13} />} Candidates ({n})
        </button>
      </div>
      {open && (
        <div className="px-4 py-3 space-y-3">
          <div className="border border-[#FFCC00]/40 bg-[#FFCC00]/5 px-3 py-2 text-[11px] text-zinc-300 leading-relaxed" data-testid="candidate-explorer-rule">
            <Warning size={12} className="inline mr-1 text-[#FFCC00]" />
            <span className="font-semibold text-[#FFCC00]">How promoting works:</span> your portfolio stays at {portfolio.length} lineups, so promoting a candidate
            <span className="text-white font-semibold"> replaces your lowest-$EV lineup</span>
            {lowest ? <> (currently <span className="text-white font-semibold">Lineup {lowest.lineup_id} · {money(lowest.payout_ev)} $EV</span>)</> : null}.
            You'll confirm the exact lineup that drops before anything changes, and you can pick a different one or add instead.
          </div>
          <div className="flex items-center gap-2 flex-wrap mono text-[9px] uppercase tracking-widest">
            <span className="text-zinc-600">Sort</span>
            {SORTS.map((s) => (
              <button key={s.key} type="button" onClick={() => setSort(s.key)} data-testid={`candidate-sort-${s.key}`} aria-pressed={sort === s.key}
                      className={`px-2 py-0.5 border ${sort === s.key ? "bg-white text-black border-white font-bold" : "border-zinc-800 text-zinc-500 hover:text-zinc-200"}`}>{s.label}</button>
            ))}
            <span className="text-zinc-600 ml-3">Why rejected</span>
            {REASONS.map((r) => {
              const c = r.key === "all" ? n : rejected.filter((x) => x.reject_code === r.key).length;
              if (r.key !== "all" && c === 0) return null;
              return (
                <button key={r.key} type="button" onClick={() => setReason(r.key)} data-testid={`candidate-reason-${r.key}`} aria-pressed={reason === r.key}
                        className={`px-2 py-0.5 border ${reason === r.key ? "bg-[#39FF14] text-black border-[#39FF14] font-bold" : "border-zinc-800 text-zinc-500 hover:text-zinc-200"}`}>
                  {r.label} <span className={reason === r.key ? "text-black/70" : "text-zinc-600"}>{c}</span>
                </button>
              );
            })}
          </div>
          <div className="max-h-[560px] overflow-auto border border-zinc-900">
            <table className="w-full text-xs mono" data-testid="candidate-table">
              <thead className="sticky top-0 bg-[#0e0e0e] text-[9px] uppercase tracking-widest text-zinc-500">
                <tr>
                  <th className="text-left px-3 py-2">#</th>
                  <th className="text-right px-2 py-2">$EV</th>
                  <th className="text-right px-2 py-2">Proj</th>
                  <th className="text-right px-2 py-2">Own</th>
                  <th className="text-left px-2 py-2">Shape</th>
                  <th className="text-left px-2 py-2">Roster</th>
                  <th className="text-left px-2 py-2">Why the sim passed</th>
                  <th className="px-2 py-2" />
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.candidate_id} className="border-t border-zinc-900 hover:bg-zinc-900/40" data-testid={`candidate-row-${c.candidate_id}`}>
                    <td className="px-3 py-2 text-zinc-500">C-{c.candidate_id}</td>
                    <td className="px-2 py-2 text-right text-white">{money(c.payout_ev)}</td>
                    <td className="px-2 py-2 text-right">{Number(c.projection || 0).toFixed(1)}</td>
                    <td className="px-2 py-2 text-right text-zinc-400">{Number(c.total_own || 0).toFixed(0)}%</td>
                    <td className="px-2 py-2 text-zinc-400">{shapeLabel(c.stack_type)}</td>
                    <td className="px-2 py-2 text-zinc-300 max-w-[420px] truncate" title={(c.players || []).map((p) => `${p.position} ${p.name}`).join(" · ")}>
                      {(c.players || []).map((p) => p.name?.split(" ").slice(-1)[0]).join(" · ")}
                    </td>
                    <td className="px-2 py-2 text-zinc-400 text-[10px]">{c.reject_reason}</td>
                    <td className="px-2 py-2 text-right whitespace-nowrap">
                      <button type="button" onClick={() => startPromote(c, "swap")} data-testid={`candidate-promote-${c.candidate_id}`}
                              className="inline-flex items-center gap-1 px-2 py-1 border border-[#39FF14]/60 text-[#39FF14] hover:bg-[#39FF14] hover:text-black text-[9px] uppercase tracking-widest font-bold">
                        <ArrowUp size={11} /> Promote
                      </button>
                    </td>
                  </tr>
                ))}
                {!rows.length && (
                  <tr><td colSpan={8} className="px-3 py-6 text-center text-zinc-600">Nothing here for that filter.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {pending && (
        <div className="fixed inset-0 z-[80] bg-black/70 flex items-center justify-center p-4" data-testid="candidate-promote-confirm">
          <div className="bg-[#0e0e0e] border-2 border-[#39FF14] max-w-lg w-full p-5 space-y-4">
            <div className="overline text-[#39FF14]">Promote candidate C-{pending.cand.candidate_id}</div>
            <div className="text-sm text-zinc-200 leading-relaxed">
              <span className="text-white font-semibold">{money(pending.cand.payout_ev)} $EV · {Number(pending.cand.projection || 0).toFixed(1)} proj</span>
              <div className="text-[11px] text-zinc-400 mt-1">{(pending.cand.players || []).map((p) => `${p.position} ${p.name}`).join(" · ")}</div>
            </div>
            <div className="flex gap-2 mono text-[10px] uppercase tracking-widest">
              <button type="button" onClick={() => setPending({ ...pending, mode: "swap" })} aria-pressed={pending.mode === "swap"} data-testid="candidate-mode-swap"
                      className={`inline-flex items-center gap-1 px-3 py-1.5 border ${pending.mode === "swap" ? "bg-white text-black border-white font-bold" : "border-zinc-700 text-zinc-400"}`}>
                <ArrowsLeftRight size={12} /> Swap (keep {portfolio.length})
              </button>
              <button type="button" onClick={() => setPending({ ...pending, mode: "add" })} aria-pressed={pending.mode === "add"} data-testid="candidate-mode-add"
                      className={`inline-flex items-center gap-1 px-3 py-1.5 border ${pending.mode === "add" ? "bg-white text-black border-white font-bold" : "border-zinc-700 text-zinc-400"}`}>
                <Plus size={12} /> Add instead ({portfolio.length + 1})
              </button>
            </div>
            {pending.mode === "swap" ? (
              <div className="border border-[#FF3B30]/50 bg-[#FF3B30]/5 p-3 text-[12px] text-zinc-200" data-testid="candidate-victim-box">
                <div className="font-semibold text-[#FF3B30] uppercase tracking-widest text-[10px] mb-1">This lineup will be removed</div>
                <select value={pending.victimId ?? ""} onChange={(e) => setPending({ ...pending, victimId: Number(e.target.value) })}
                        data-testid="candidate-victim-select" className="w-full bg-black border border-zinc-700 px-2 py-1.5 mono text-[12px]">
                  {[...portfolio].sort((a, b) => (Number(a.payout_ev) || 0) - (Number(b.payout_ev) || 0)).map((L) => (
                    <option key={L.lineup_id} value={L.lineup_id}>
                      Lineup {L.lineup_id} · {money(L.payout_ev)} $EV · {Number(L.projection || 0).toFixed(1)} proj{L.lineup_id === lowest?.lineup_id ? " · lowest $EV (default)" : ""}
                    </option>
                  ))}
                </select>
                <div className="text-[10px] text-zinc-500 mt-1">Default is your lowest-$EV lineup. Pick another if you want to protect it.</div>
              </div>
            ) : (
              <div className="text-[11px] text-zinc-400">No lineup is removed — your portfolio grows to {portfolio.length + 1}. Make sure you have the entries for it.</div>
            )}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setPending(null)} data-testid="candidate-promote-cancel"
                      className="px-3 py-1.5 border border-zinc-700 text-zinc-300 mono text-[10px] uppercase tracking-widest">Cancel</button>
              <button type="button" onClick={confirm} disabled={busy} data-testid="candidate-promote-confirm-btn"
                      className="px-4 py-1.5 bg-[#39FF14] text-black font-black mono text-[10px] uppercase tracking-widest disabled:opacity-40">
                {busy ? "Promoting…" : pending.mode === "swap" ? `Replace Lineup ${pending.victimId ?? "?"}` : "Add to portfolio"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default CandidateExplorer;
